import { useEffect, useState } from "react";
import type { Lead, Salesperson } from "../../types/lead";
import type { Location } from "../../types/location";
import type { OrgLocation } from "../../config/locations";
import { setActiveLeadId, clearActiveLeadId } from "../../lib/activeLeadIdSurface";
import type { PrimaryMode } from "./sections/LeadWorkspaceHeaderAccess";
import type { WorkspaceToast } from "../shell/WorkspaceToastOverlay";
import {
  readLastPrimaryMode,
  writeLastPrimaryMode,
} from "../../lib/lastPrimaryModeStorage";

export type MergeSuggestion = {
  sourceId: string;
  reason: string;
};

const EMPTY_LEAD_AUTO_DELETE_KEY = "bb-setting-empty-lead-auto-delete";

export function useLeadWorkspaceState() {
  // The `status` / `setStatus` workspace-wide state retired in Slice F
  // of docs/planning/activity_dock_workflow_facilitation.md §6. Slice B
  // had already removed the only visible render (the `Status: …`
  // paragraph) and rerouted the one load-bearing string ("Select your
  // location to continue") to a role-gated banner; this commit removes
  // the ~30 dead-write callsites that were left behind (ASR engine,
  // GHL sender, lead actions, dev mode, extraction effects, etc.). The
  // signal channel was never a substitute for proper toasts/banners,
  // and rebuilding workflow notifications on top of toast / dock
  // sections is much easier with no parallel string-state competing.
  const [transcript, setTranscript] = useState("");
  const [leads, setLeads] = useState<Lead[]>([]);
  const [currentLead, setCurrentLead] = useState<Lead | null>(null);
  const [salesperson, setSalesperson] = useState<Salesperson | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [lastSendAt, setLastSendAt] = useState<number | null>(null);
  const [lastSendStatus, setLastSendStatus] = useState<"success" | "error" | null>(null);
  const [phoneDraft, setPhoneDraft] = useState("");
  // Slice B3 (Lead Control MVP polish): the optional `action` field on
  // `WorkspaceToast` is what lets the post-submit toast surface a
  // clickable CRM deep-link. Inline-shaped useState earlier dropped that
  // field on the floor, which broke the type contract with
  // `useLeadWorkspaceRuntimeBasics`'s auto-dismiss logic
  // (`params.toast.action ? 6000 : 3000`) and tanked the Vercel build.
  const [toast, setToast] = useState<WorkspaceToast | null>(null);
  const [emptyLeadAutoDeleteEnabled, setEmptyLeadAutoDeleteEnabled] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(EMPTY_LEAD_AUTO_DELETE_KEY);
      if (v == null) return true;
      return v === "true";
    } catch {
      return true;
    }
  });
  const [mergeSuggestion, setMergeSuggestion] = useState<MergeSuggestion | null>(null);
  const [locations, setLocations] = useState<OrgLocation[]>([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [showAllRecentLeads, setShowAllRecentLeads] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(true);
  const [showLocationDetails, setShowLocationDetails] = useState(false);
  const [showTranscriptDetails, setShowTranscriptDetails] = useState(false);
  const [recentFilter, setRecentFilter] = useState<"all" | "today" | "unsent">("all");
  const [recentSort, setRecentSort] = useState<"recent" | "oldest" | "status">("recent");
  const [compactView, setCompactView] = useState(false);
  const [newTagInput, setNewTagInput] = useState("");
  const [pendingSendLead, setPendingSendLead] = useState<Lead | null>(null);
  const [pendingLocationSend, setPendingLocationSend] = useState<Lead | null>(null);
  const [duplicateMatches, setDuplicateMatches] = useState<Lead[]>([]);
  const [duplicateReason, setDuplicateReason] = useState<string | null>(null);
  const [isStoppingUi, setIsStoppingUi] = useState(false);
  const [trainingMode, setTrainingMode] = useState(false);
  // The lazy initializer reads any prior session's last mode (per
  // dev-sync note f4ffe4de). The role-aware fallback runs in
  // `LeadWorkspaceShell` once auth bootstraps, because role data isn't
  // available synchronously here.
  const [mode, setMode] = useState<PrimaryMode>(() => readLastPrimaryMode() ?? "leads");

  useEffect(() => {
    writeLastPrimaryMode(mode);
  }, [mode]);

  useEffect(() => {
    try {
      localStorage.setItem(EMPTY_LEAD_AUTO_DELETE_KEY, String(emptyLeadAutoDeleteEnabled));
    } catch {
      // ignore
    }
  }, [emptyLeadAutoDeleteEnabled]);

  // Publish the active lead id to the module-level surface so the
  // bug-report payload can enrich `state_snapshot` + `devSyncMetadata`
  // without prop-threading. See docs/planning/bug_report_active_context.md.
  useEffect(() => {
    setActiveLeadId(currentLead?.id ?? null);
    return () => clearActiveLeadId();
  }, [currentLead?.id]);

  return {
    transcript,
    setTranscript,
    leads,
    setLeads,
    currentLead,
    setCurrentLead,
    salesperson,
    setSalesperson,
    location,
    setLocation,
    isListening,
    setIsListening,
    showProfileEditor,
    setShowProfileEditor,
    lastSendAt,
    setLastSendAt,
    lastSendStatus,
    setLastSendStatus,
    phoneDraft,
    setPhoneDraft,
    toast,
    setToast,
    emptyLeadAutoDeleteEnabled,
    setEmptyLeadAutoDeleteEnabled,
    mergeSuggestion,
    setMergeSuggestion,
    locations,
    setLocations,
    locationsLoaded,
    setLocationsLoaded,
    showAllRecentLeads,
    setShowAllRecentLeads,
    showProductDetails,
    setShowProductDetails,
    showLocationDetails,
    setShowLocationDetails,
    showTranscriptDetails,
    setShowTranscriptDetails,
    recentFilter,
    setRecentFilter,
    recentSort,
    setRecentSort,
    compactView,
    setCompactView,
    newTagInput,
    setNewTagInput,
    pendingSendLead,
    setPendingSendLead,
    pendingLocationSend,
    setPendingLocationSend,
    duplicateMatches,
    setDuplicateMatches,
    duplicateReason,
    setDuplicateReason,
    isStoppingUi,
    setIsStoppingUi,
    trainingMode,
    setTrainingMode,
    mode,
    setMode,
  };
}
